<template>
  <div class="flex flex-col items-center justify-center py-12 px-6 max-w-xl mx-auto text-center">
    <!-- Icon and Header -->
    <div class="mb-8">
      <h1 class="text-2xl font-medium mb-2">Update Category/Subcategory</h1>
      <p class="text-gray-400">
        Select category and subcategory to update.
      </p>
    </div>

    <!-- Category Section -->
    <v-card class="w-full mb-6" outlined>
      <v-card-title>Category</v-card-title>
      <v-card-text>
        <v-select
          v-model="selectedCategory"
          :items="categories"
          item-text="name"
          item-value="id"
          label="Select Category"
          placeholder="Choose a category"
          outlined
          clearable
          class="mb-4"
        ></v-select>

        <div v-if="selectedCategory">
          <v-text-field
            v-model="categoryUpdateText"
            label="New Category Name"
            placeholder="Enter new category name"
            outlined
            class="mb-4"
          ></v-text-field>

          <v-btn
            :disabled="!isCategoryUpdateEnabled"
            color="secondary"
            :loading="updatingCategory"
            @click="updateCategory"
          >
            Update Category
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Subcategory Section -->
    <v-card v-if="selectedCategory" class="w-full" outlined>
      <v-card-title>Subcategory</v-card-title>
      <v-card-text>
        <v-select
          v-model="selectedSubcategory"
          :items="subcategories"
          item-text="name"
          item-value="id"
          label="Select Subcategory"
          placeholder="Choose a subcategory"
          outlined
          clearable
          :loading="loading"
          class="mb-4"
        ></v-select>

        <div v-if="selectedSubcategory">
          <v-text-field
            v-model="subcategoryUpdateText"
            label="New Subcategory Name"
            placeholder="Enter new subcategory name"
            outlined
            class="mb-4"
          ></v-text-field>

          <v-btn
            :disabled="!isSubcategoryUpdateEnabled"
            color="secondary"
            :loading="updatingSubcategory"
            @click="updateSubcategory"
          >
            Update Subcategory
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import HTTP from '@/api/owl';

export default {
  data() {
    return {
      categories: [],
      subcategories: [],
      loading: false,
      selectedCategory: null,
      selectedSubcategory: null,
      categoryUpdateText: '',
      subcategoryUpdateText: '',
      updatingCategory: false,
      updatingSubcategory: false,
    };
  },
  computed: {
    isCategoryUpdateEnabled() {
      return this.categoryUpdateText && this.categoryUpdateText.trim().length > 0;
    },
    isSubcategoryUpdateEnabled() {
      return this.subcategoryUpdateText && this.subcategoryUpdateText.trim().length > 0;
    },
  },
  watch: {
    selectedCategory(newVal) {
      if (newVal) {
        this.fetchSubcategories();
        this.selectedSubcategory = null;
        this.subcategoryUpdateText = '';
      } else {
        this.subcategories = [];
        this.selectedSubcategory = null;
        this.subcategoryUpdateText = '';
      }
    },
    selectedSubcategory() {
      this.subcategoryUpdateText = '';
    },
  },
  mounted() {
    this.fetchCategories();
  },
  methods: {
    fetchCategories() {
      HTTP.get('/categories')
        .then(({ data }) => {
          this.categories = data;
        })
        .catch(error => {
          console.error('Error fetching categories:', error);
          this.$toast.error('Failed to fetch categories');
        });
    },
    fetchSubcategories() {
      if (!this.selectedCategory) return;

      this.loading = true;
      return HTTP.get(`/categories/${this.selectedCategory}/sub_categories`)
        .then(({ data }) => {
          this.subcategories = data;
        })
        .catch(error => {
          console.error('Error fetching subcategories:', error);
          this.$toast.error('Failed to fetch subcategories');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    updateCategory() {
      if (!this.isCategoryUpdateEnabled) return;

      this.updatingCategory = true;
      HTTP.put(`/categories/${this.selectedCategory}`, {
        name: this.categoryUpdateText.trim(),
      })
        .then(({ data }) => {
          this.$flash('success', 'Category updated successfully');
          this.categoryUpdateText = '';
          // Update the categories list to reflect the change
          const categoryIndex = this.categories.findIndex(cat => cat.id === this.selectedCategory);
          if (categoryIndex !== -1) {
            this.categories[categoryIndex] = data;
          }
        })
        .catch(error => {
          console.error('Error updating category:', error);
          this.$flash('error', 'Failed to update category');
        })
        .finally(() => {
          this.updatingCategory = false;
        });
    },
    updateSubcategory() {
      if (!this.isSubcategoryUpdateEnabled) return;

      this.updatingSubcategory = true;
      HTTP.put(`/sub_categories/${this.selectedSubcategory}`, {
        name: this.subcategoryUpdateText.trim(),
      })
        .then(({ data }) => {
          this.$flash('success', 'Subcategory updated successfully');
          this.subcategoryUpdateText = '';
          // Update the subcategories list to reflect the change
          const subcategoryIndex = this.subcategories.findIndex(
            subcat => subcat.id === this.selectedSubcategory,
          );
          if (subcategoryIndex !== -1) {
            this.subcategories[subcategoryIndex] = data;
          }
        })
        .catch(error => {
          console.error('Error updating subcategory:', error);
          this.$flash('error', 'Failed to update subcategory');
        })
        .finally(() => {
          this.updatingSubcategory = false;
        });
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
